import {
  Box,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useColors from "../../../api/hooks/useColors";
import SvgToIcon from "../../../icons/SvgToIcon";
import { icons } from "../../stack/environment/icons/iconExport";

const CustomMenu = ({
  title = "Filter",
  anchorElMenu,
  openMenu,
  handleCloseMenu,
  moreActions,
  appliedFilters = {},
  clearButton = false,
}) => {
  const colors = useColors();
  const [loadingActionIndex, setLoadingActionIndex] = useState(-1);
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState(null);
  const [activeSubMenu, setActiveSubMenu] = useState(null);

  // Update active submenu when moreActions changes (to reflect updated selection states)
  useEffect(() => {
    if (activeSubMenu && moreActions) {
      const updatedSubMenu = moreActions.find(
        (action) => action.key === activeSubMenu.key
      );
      if (
        updatedSubMenu &&
        (updatedSubMenu.submenu || updatedSubMenu.subMenu)
      ) {
        setActiveSubMenu(updatedSubMenu);
      }
    }
  }, [moreActions, activeSubMenu]);

  const handleActionClick = async (event, item, index) => {
    // If item has submenu, open it instead of executing action
    if (item.submenu || item.customSubmenu) {
      setSubMenuAnchorEl(event.currentTarget);
      setActiveSubMenu(item);
      return;
    }

    setLoadingActionIndex(index);
    try {
      await item.onclick(event);
    } finally {
      setLoadingActionIndex(-1);
    }
  };

  const handleSubMenuClose = () => {
    setSubMenuAnchorEl(null);
    setActiveSubMenu(null);
  };

  const handleSubMenuItemClick = (event, subItem) => {
    if (subItem.onclick) {
      subItem.onclick(event);
    }
  };

  return (
    <>
      <Menu
        id="more-info-menu"
        anchorEl={anchorElMenu}
        open={openMenu}
        onClose={handleCloseMenu}
        sx={{
          "& .MuiMenu-list": {
            padding: "0",
          },
        }}
        PaperProps={{
          style: {
            borderRadius: "8px",
            padding: "4px",
            minWidth: "190px",
            boxShadow: "0px 1px 4px 0px " + colors.grey_48percent,
          },
        }}
      >
        {title && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginBottom: "4px",
            }}
          >
            <Typography
              variant="overline"
              sx={{
                padding: "8px 4px",
                color: colors.text_secondary,
              }}
            >
              {title}
            </Typography>
            <Divider />
          </Box>
        )}
        {moreActions.map((item, index) => {
          if (item.type === "button") {
            return (
              <Box>
                <Divider />
                <MenuItem
                  key={item.key}
                  onClick={(event) => handleActionClick(event, item, index)}
                  disabled={loadingActionIndex === index || item.disabled}
                  sx={{
                    padding: "8px 4px",
                    color:
                      appliedFilters && appliedFilters[item.key]
                        ? colors.primary_main
                        : colors.text_primary,
                    "&:hover": {
                      backgroundColor: colors.grey_4percent,
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: colors.primary_main }}
                  >
                    {item.title}
                  </Typography>
                </MenuItem>
              </Box>
            );
          }
          return (
            <MenuItem
              key={item.key}
              onClick={(event) => handleActionClick(event, item, index)}
              disabled={loadingActionIndex === index}
              sx={{
                padding: "8px 4px",
                color:
                  appliedFilters && appliedFilters[item.key]
                    ? colors.primary_main
                    : colors.text_primary,
                "&:hover": {
                  backgroundColor: colors.grey_4percent,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "8px",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {item.icon && (
                    <SvgToIcon
                      IconLogo={item.icon}
                      style={{ width: "16px", height: "16px" }}
                    />
                  )}
                  <Typography variant="caption">{item.title}</Typography>
                </Box>
                {loadingActionIndex === index && <CircularProgress size={16} />}
                {(item.submenu || item.customSubmenu) && (
                  <SvgToIcon
                    IconLogo={item.trailingIcon}
                    style={{ width: "14px", height: "12px" }}
                  />
                )}
                {item.trailingIcon && !item.submenu && !item.customSubmenu && (
                  <SvgToIcon
                    IconLogo={item.trailingIcon}
                    style={{ width: "14px", height: "12px" }}
                  />
                )}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
      {activeSubMenu && activeSubMenu.customSubmenu ? (
        <activeSubMenu.customSubmenu.component
          {...activeSubMenu.customSubmenu.props}
          menuAnchorEl={subMenuAnchorEl}
          setMenuAnchorEl={setSubMenuAnchorEl}
        />
      ) : (
        activeSubMenu &&
        (activeSubMenu.submenu || activeSubMenu.subMenu) && (
          <Menu
            id="sub-menu"
            anchorEl={subMenuAnchorEl}
            open={Boolean(subMenuAnchorEl)}
            onClose={handleSubMenuClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              "& .MuiMenu-list": {
                padding: "4px 0",
              },
            }}
            PaperProps={{
              style: {
                borderRadius: "8px",
                padding: "4px 8px",
                minWidth: "160px",
                boxShadow: "0px 2px 8px 0px " + colors.grey_48percent,
              },
            }}
          >
            {activeSubMenu.submenuTitle && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  marginBottom: "4px",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    padding: "8px 4px",
                    color: colors.text_secondary,
                  }}
                >
                  {activeSubMenu.submenuTitle}
                </Typography>
                <Divider />
              </Box>
            )}
            {(activeSubMenu.submenu || activeSubMenu.subMenu).map(
              (subItem, subIndex) => (
                <MenuItem
                  key={subItem.key || subIndex}
                  onClick={(event) => handleSubMenuItemClick(event, subItem)}
                  disabled={subItem.disabled}
                  selected={subItem.selected}
                  sx={{
                    color: colors.text_primary,
                    borderRadius: "6px",
                    height: "36px",
                    minHeight: "36px",
                    padding: "4px",
                    "&.Mui-selected": {
                      backgroundColor: activeSubMenu?.submenuMultiple
                        ? "transparent"
                        : colors.grey_8percent,
                      "&:hover": {
                        backgroundColor: colors.grey_4percent,
                      },
                    },
                    "&:hover": {
                      backgroundColor: colors.grey_4percent,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "8px",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                    >
                      {activeSubMenu?.submenuMultiple &&
                        (subItem.selected ? (
                          <SvgToIcon
                            IconLogo={icons.Checked}
                            style={{
                              width: "16px",
                              height: "16px",
                              color: colors.action_active,
                            }}
                          />
                        ) : (
                          <SvgToIcon
                            IconLogo={icons.Unchecked}
                            style={{ width: "16px", height: "16px" }}
                          />
                        ))}
                      {subItem.icon && (
                        <SvgToIcon
                          IconLogo={subItem.icon}
                          style={{ width: "16px", height: "16px" }}
                        />
                      )}
                      {subItem.title instanceof Object ? (
                        subItem.title
                      ) : (
                        <Typography variant="caption">
                          {subItem.title}
                        </Typography>
                      )}
                    </Box>
                    {subItem.trailingIcon && (
                      <SvgToIcon
                        IconLogo={subItem.trailingIcon}
                        style={{ width: "14px", height: "12px" }}
                      />
                    )}
                    {subItem.selected && !activeSubMenu?.submenuMultiple && (
                      <SvgToIcon
                        IconLogo={icons.DoneIcon}
                        style={{ width: "16px", height: "16px" }}
                      />
                    )}
                  </Box>
                </MenuItem>
              )
            )}
          </Menu>
        )
      )}
    </>
  );
};

export default CustomMenu;
